import { Component, inject, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../ui';
import { minDateValidator } from '../../../shared/validators/min-date.validator';
import { maxDateValidator } from '../../../shared/validators/max-date.validator';
import { ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSpinnerComponent,
} from '../../../ui';

export interface MoveStartDateDialogData {
  contractId: number;
  invoiceDay: number;
  minDate: string;
  maxDate: string;
  currentPlan: string;
  currentStartDate: string;
}

@Component({
  selector: 'cmt-move-start-date-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmInputDirective, HlmSpinnerComponent,
  ],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideCalendarRange" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Move Contract Start Date</h2>
          <p hlmDialogDescription>
            Contract
            <span class="font-mono-feat">{{ data.contractId }}</span> ·
            <strong>{{ data.currentPlan }}</strong> ·
            current start: <span class="font-mono-feat">{{ data.currentStartDate }}</span>
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <form [formGroup]="form" class="flex flex-col gap-4">
          <hlm-form-field>
            <label hlmLabel for="startDate">New start date</label>
            <input id="startDate" hlmInput type="date" formControlName="startDate"
                   [min]="minDateStr" [max]="maxDateStr" />
            <p hlmFieldHint>
              Range: <span class="font-mono-feat">{{ data.minDate }}</span>
              @if (data.maxDate) {
                — <span class="font-mono-feat">{{ data.maxDate }}</span>
              }
            </p>
            @if (form.controls.startDate.hasError('minDate')) {
              <p hlmFieldError>The new start date cannot be before {{ minDateStr }}.</p>
            }
            @if (form.controls.startDate.hasError('maxDate')) {
              <p hlmFieldError>The new start date cannot be after {{ maxDateStr }}.</p>
            }
          </hlm-form-field>
        </form>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Moving...
          } @else {
            <hlm-icon name="lucideCalendarRange" size="0.9rem" />
            Move
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class MoveStartDateDialogComponent {
  readonly data = inject<MoveStartDateDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<MoveStartDateDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);

  readonly submitting = signal(false);
  readonly minDateStr = this.data.minDate;
  readonly maxDateStr = this.data.maxDate || '';

  readonly form: FormGroup = this.fb.group({
    // `[min]`/`[max]` on a date input are presentational only - the real checks live here.
    startDate: [
      null,
      [
        Validators.required,
        minDateValidator(() => this.minDateStr),
        maxDateValidator(() => this.maxDateStr),
      ],
    ],
  });

  canSubmit(): boolean {
    return this.form.valid && !this.submitting();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);

    const startDate = this.form.value.startDate;

    this.authService.user$.subscribe((user) => {
      this.contractsService.modifyContract(
        this.data.contractId,
        [{ type: 'REPLACE', attribute: 'startDate' }],
        { startDate, employeeId: String(user?.userId ?? 0) },
      ).subscribe({
        next: () => {
          this.toast.open('Start date moved successfully', 'OK', { duration: 4000 });
          this.dialogRef.close(true);
        },
        error: () => {
          this.toast.open('Failed to move start date', 'OK', { duration: 6000 });
          this.submitting.set(false);
        },
      });
    }).unsubscribe();
  }
}
